/**
 * *To* (a text, an email) and *With* (a meeting) — one contact, picked from
 * the same search *Attached to* uses (§9.2), contacts only.
 *
 * The box shows what the sheet needs of them: the mobile for a text (*Renee
 * Castillo (717) 555-0193 · Mobile*), the address for an email, the name
 * alone for a meeting (Board 11 B, D, F).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { contactTone, initialsOf, toneOf, type ComposeContact } from '@rowted/shared'
import { Popover } from '../../components/overlays.js'
import { PanelSheet } from '../../components/panel-sheet.js'
import { BackLayer } from '../use-activity-history.js'
import { cn } from '../../lib/cn.js'
import { Glyph } from '../glyphs.js'
import { activityRequest } from '../api.js'
import { Highlight } from '../../requests/search-box.js'
import { FOCUS, PICKER_LAYER, boxClass } from './kit.js'

export interface ContactHit { id: string; label: string; sub: string }
type Hit = ContactHit

/** *Attached to*'s own search, contacts only — what *To* has always asked. */
const attachContacts = (q: string): Promise<Hit[]> =>
  activityRequest<{ contacts: Hit[] }>(`/activity/attach?q=${encodeURIComponent(q)}`).then((answer) => answer.contacts)

export function ContactFace({ id, name, size = 24 }: { id: string; name: string; size?: number }) {
  const tone = toneOf(contactTone(id))
  return (
    <span className="inline-flex shrink-0 items-center justify-center rounded-full font-bold tracking-[.02em]"
      style={{ width: size, height: size, background: tone.tint, color: tone.text, fontSize: 10 }}>
      {initialsOf(name)}
    </span>
  )
}

function Search({ onPick, phone, onClose, clearLabel, onClear, onGrow, search }: {
  onPick: (hit: Hit) => void; phone: boolean; onClose: () => void; clearLabel?: string; onClear?: () => void
  onGrow: () => void; search: (q: string) => Promise<Hit[]>
}) {
  const [q, setQ] = useState('')
  const [hits, setHits] = useState<Hit[]>([])
  useEffect(() => {
    let live = true
    if (!q.trim()) { setHits([]); return undefined }
    const timer = window.setTimeout(() => {
      search(q.trim())
        .then((found) => { if (live) { setHits(found); onGrow() } })
        .catch(() => { if (live) setHits([]) })
    }, 150)
    return () => { live = false; window.clearTimeout(timer) }
  }, [q])
  const words = q.trim().split(/\s+/).filter((word) => word.length > 1)
  return (
    <div className="flex flex-col">
      <div className={phone ? 'px-4 pb-1 pt-2.5' : 'px-2.5 pb-1 pt-2'}>
        <label className={cn('flex items-center gap-2 rounded-lg border border-line px-2.5', FOCUS, phone ? 'h-[46px] text-[15px]' : 'h-9 text-[13px]')}>
          <span className="text-text-muted"><Glyph name="search" size={15} /></span>
          <input autoFocus value={q} onChange={(event) => setQ(event.target.value)} aria-label="Find a contact"
            placeholder="Find a contact"
            onKeyDown={(event) => {
              if (event.key === 'Enter' && hits[0]) { event.preventDefault(); onPick(hits[0]) }
              if (event.key === 'Escape') { event.stopPropagation(); onClose() }
            }}
            className="min-w-0 flex-1 bg-transparent outline-none focus-visible:shadow-none placeholder:text-text-faint" />
        </label>
      </div>
      <div role="listbox" aria-label="Contacts" className="flex flex-col py-1">
        {clearLabel && onClear && (
          <button type="button" role="option" aria-selected={false} onClick={onClear}
            className={cn('flex w-full items-center gap-2.5 text-left text-text-muted hover:bg-hover',
              phone ? 'min-h-[52px] px-4 text-[15px]' : 'px-3 py-[7px] text-[13px]')}>
            {clearLabel}
          </button>
        )}
        {hits.map((hit) => (
          <button key={hit.id} type="button" role="option" aria-selected={false} onClick={() => onPick(hit)}
            className={cn('flex w-full items-center gap-2.5 text-left hover:bg-hover', phone ? 'min-h-[52px] px-4' : 'px-3 py-[7px]')}>
            <ContactFace id={hit.id} name={hit.label} size={phone ? 30 : 26} />
            <span className="flex min-w-0 flex-col">
              <b className={cn('truncate font-semibold', phone ? 'text-[15px]' : 'text-[13px]')}><Highlight text={hit.label} words={words} /></b>
              {hit.sub && <span className="truncate text-[11.5px] text-text-muted">{hit.sub}</span>}
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}

export function ContactField({
  label, contact, onPick, onClear, clearLabel, phone, detail, placeholder = 'A contact…', tall = false,
  search = attachContacts,
}: {
  label: string
  contact: ComposeContact | null
  onPick: (contactId: string) => void
  /** A first row that takes the contact away — *Nobody from outside* (Board 20 A's *With*). */
  onClear?: () => void
  clearLabel?: string
  phone: boolean
  /** What follows the name — *(717) 555-0193 · Mobile*, the address — or nothing. */
  detail?: ReactNode
  placeholder?: string
  /** The 38 px *To* box (Board 11 B, D) rather than the 36 px *With* (Board 11 F). */
  tall?: boolean
  /** Who answers the typing — *Attached to*'s search unless the sheet brings its own (a meeting's). */
  search?: (q: string) => Promise<Hit[]>
}) {
  const [open, setOpen] = useState(false)
  const [, grow] = useState(0)
  const button = useRef<HTMLButtonElement>(null)
  return (
    <>
      <span className="flex items-center">
        <button ref={button} type="button" aria-label={label} aria-haspopup="dialog" onClick={() => setOpen(true)}
          className={boxClass(phone, cn('text-left', tall && !phone && 'h-[38px] gap-2 px-2', !tall && !phone && 'gap-2 px-2'))}>
          {contact ? (
            <>
              <ContactFace id={contact.id} name={contact.name} size={tall ? 24 : 22} />
              <b className="shrink-0 text-[13px]">{contact.name}</b>
              {detail && <span className="min-w-0 truncate text-[12px] text-text-muted">{detail}</span>}
            </>
          ) : <span className="px-1 text-text-faint">{placeholder}</span>}
          <span className="flex-1" />
          {!phone && <span className="shrink-0 text-text-muted"><Glyph name="chevronDown" size={14} /></span>}
        </button>
      </span>
      {/* Back closes the picker first (Aaron, Oct 26). */}
      {phone && open && <BackLayer onBack={() => setOpen(false)} />}
      {phone ? (
        <PanelSheet open={open} onClose={() => setOpen(false)} title={label} layer={PICKER_LAYER}>
          {open && <Search phone onPick={(hit) => { setOpen(false); onPick(hit.id) }} onClose={() => setOpen(false)}
            clearLabel={contact ? clearLabel : undefined} onClear={() => { setOpen(false); onClear?.() }}
            onGrow={() => grow((n) => n + 1)} search={search} />}
        </PanelSheet>
      ) : (
        <Popover open={open} onClose={() => setOpen(false)} anchor={button.current} label={label} width={380}
          layer={PICKER_LAYER} maxHeight={360} className="py-1">
          {open && <Search phone={false} onPick={(hit) => { setOpen(false); onPick(hit.id) }} onClose={() => setOpen(false)}
            clearLabel={contact ? clearLabel : undefined} onClear={() => { setOpen(false); onClear?.() }}
            onGrow={() => grow((n) => n + 1)} search={search} />}
        </Popover>
      )}
    </>
  )
}
